// src/firebase/cart-functions.js

import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db, DEFAULT_STORE_ID } from "./firebase";
import { enrichCartItemsWithPricing } from "../utils/productPricing";

/**
 * Generate display variant text for cart items
 * @param {object} product - Product data
 * @param {object} variant - Variant data
 * @returns {string} Display variant text for English (can be localized later)
 */
const generateDisplayVariant = (product, variant) => {
    if (!variant) return null;

    const variantParts = [];

    // If product has options, use them to generate display text
    if (product.options && Array.isArray(product.options)) {
        product.options.forEach(option => {
            // Get option key (always use English for lookup)
            const optionKey = typeof option.name === 'string'
                ? option.name.toLowerCase()
                : (option.name?.en || Object.values(option.name || {})[0] || '').toLowerCase();

            const value = variant[optionKey];
            if (value) {
                // Get option display name (use English for now)
                const optionName = typeof option.name === 'string'
                    ? option.name
                    : (option.name?.en || Object.values(option.name || {})[0] || '');

                let displayValue = value;

                // Handle multilingual values - use English for cart storage
                try {
                    const valueData = JSON.parse(value);
                    if (typeof valueData === 'object' && valueData !== null) {
                        displayValue = valueData.en || Object.values(valueData)[0] || value;
                    }
                } catch {
                    displayValue = value;
                }

                if (optionName && displayValue) {
                    variantParts.push(`${optionName}: ${displayValue}`);
                }
            }
        });
    } else {
        // Fallback: use common variant properties
        const commonProps = ['color', 'size', 'material', 'style', 'type'];
        commonProps.forEach(prop => {
            if (variant[prop]) {
                const propName = prop.charAt(0).toUpperCase() + prop.slice(1);
                let displayValue = variant[prop];

                // Handle multilingual values
                try {
                    const valueData = JSON.parse(displayValue);
                    if (typeof valueData === 'object' && valueData !== null) {
                        displayValue = valueData.en || Object.values(valueData)[0] || displayValue;
                    }
                } catch {
                    // Keep original value if parsing fails
                }

                if (displayValue) {
                    variantParts.push(`${propName}: ${displayValue}`);
                }
            }
        });
    }

    return variantParts.length > 0 ? variantParts.join(' • ') : null;
};

/**
 * Enrich all cart items with fresh pricing data (simplified version)
 * @param {array} cartItems - Array of cart items with stored display data
 * @returns {array} Array of enriched cart items with fresh pricing
 */
export const enrichCartWithFreshData = async (cartItems = []) => {
    try {
        return await enrichCartItemsWithPricing(cartItems, DEFAULT_STORE_ID);
    } catch (error) {
        console.error('Error enriching cart:', error);
        return cartItems; // Return original items if enrichment fails
    }
};/**
 * Updated addToCart function with multilingual variant support
 */
export const addToCart = async (product, quantity = 1, variant = null, currentUser = null, storeId = DEFAULT_STORE_ID) => {
    try {
        // Helper function to clean undefined values
        const cleanObject = (obj) => {
            const cleaned = {};
            for (const [key, value] of Object.entries(obj)) {
                if (value !== undefined) {
                    cleaned[key] = value;
                }
            }
            return cleaned;
        };

        // Create cart item with essential data for fast display
        // Store name, image AND price for immediate display
        const cartItem = cleanObject({
            // Essential cart-specific data
            productId: product.id,
            quantity: quantity,
            variantId: variant ? variant.id : null,
            storeId: storeId,
            dateAdded: new Date().toISOString(), // Use regular timestamp instead of serverTimestamp()

            // Store essential display data for fast cart rendering
            name: product.name,
            image: product.images?.[0] || null,
            displayVariant: variant ? generateDisplayVariant(product, variant) : null,

            // STORE PRICE for fast cart display (will be validated fresh at checkout)
            price: variant ? (variant.price || product.price) : product.price,
            originalPrice: variant ? (variant.originalPrice || product.originalPrice || variant.price || product.price) : (product.originalPrice || product.price),

            // Basic availability (will be validated fresh at checkout)
            inStock: variant ? (variant.quantity > 0) : (product.quantity > 0),
        });

        // console.log("🛒 Adding to cart with item:", cartItem);

        if (currentUser) {
            try {
                // User is logged in, try Firestore first
                const cartRef = doc(db, "users", currentUser.uid, "cart", "current");
                const cartDoc = await getDoc(cartRef);

                let cartData;
                if (cartDoc.exists()) {
                    cartData = cartDoc.data();

                    // Check if product with same variant already exists
                    const existingItemIndex = cartData.items.findIndex(item =>
                        item.productId === product.id &&
                        item.variantId === (variant ? variant.id : null)
                    );

                    if (existingItemIndex >= 0) {
                        // Update quantity of existing item
                        cartData.items[existingItemIndex].quantity += quantity;

                        // Update display data if variant exists (since this rarely changes)
                        if (variant) {
                            cartData.items[existingItemIndex].displayVariant = generateDisplayVariant(product, variant);
                        }

                        // Update stored display data to keep cart fast
                        cartData.items[existingItemIndex].name = product.name;
                        cartData.items[existingItemIndex].image = product.images?.[0] || null;

                        // Update pricing data for fast display
                        cartData.items[existingItemIndex].price = variant ? (variant.price || product.price) : product.price;
                        cartData.items[existingItemIndex].originalPrice = variant ? (variant.originalPrice || product.originalPrice || variant.price || product.price) : (product.originalPrice || product.price);
                    } else {
                        // Add new item
                        cartData.items.push(cartItem);
                    }
                } else {
                    // Create new cart
                    cartData = {
                        items: [cartItem],
                        storeId: storeId || DEFAULT_STORE_ID,
                        createdAt: serverTimestamp()
                    };
                }

                // Don't calculate subtotal here - it will be calculated fresh in getCart()
                // with current prices when cart is retrieved
                cartData.updatedAt = serverTimestamp();

                // Clean the entire cartData to remove any undefined values
                const cleanedCartData = cleanObject(cartData);

                // Save to Firestore
                await setDoc(cartRef, cleanedCartData);

                // Return minimal cart data - subtotal will be calculated fresh in getCart()
                return {
                    items: cartData.items,
                    subtotal: 0, // Will be calculated fresh with current prices in getCart()
                    storeId: cartData.storeId
                };
            } catch (firestoreError) {
                // console.error("Firestore permission error - using localStorage instead:", firestoreError);
                // If Firestore fails, fall back to localStorage even for logged-in users
            }
        }

        // localStorage fallback
        let cart = JSON.parse(localStorage.getItem('guestCart')) || {
            items: [],
            subtotal: 0,
            storeId: storeId
        };

        // Check if product with same variant already exists
        const existingItemIndex = cart.items.findIndex(item =>
            item.productId === product.id &&
            item.variantId === (variant ? variant.id : null)
        );

        if (existingItemIndex >= 0) {
            // Update quantity of existing item
            cart.items[existingItemIndex].quantity += quantity;

            // Update display data if variant exists (since this rarely changes)
            if (variant) {
                cart.items[existingItemIndex].displayVariant = generateDisplayVariant(product, variant);
            }

            // Update stored display data to keep cart fast
            cart.items[existingItemIndex].name = product.name;
            cart.items[existingItemIndex].image = product.images?.[0] || null;

            // Update pricing data for fast display
            cart.items[existingItemIndex].price = variant ? (variant.price || product.price) : product.price;
            cart.items[existingItemIndex].originalPrice = variant ? (variant.originalPrice || product.originalPrice || variant.price || product.price) : (product.originalPrice || product.price);
        } else {
            // Add new item
            cart.items.push(cartItem);
        }

        // Don't calculate subtotal here - it will be calculated fresh in getCart()
        // with current prices when cart is retrieved
        cart.subtotal = 0; // Placeholder - will be calculated fresh
        cart.updatedAt = new Date().toISOString();

        // Save to localStorage
        localStorage.setItem('guestCart', JSON.stringify(cart));

        return cart;
    } catch (error) {
        console.error("Error adding to cart:", error);
        throw error;
    }
};
/**
 * Get the current cart with Firestore permission error handling
 * @param {object} currentUser - Current user (if logged in)
 * @param {boolean} enrichData - Whether to enrich with fresh product data (default: false)
 * @returns {Promise<object>} Cart data
 */
export const getCart = async (currentUser = null, enrichData = false) => {
    try {
        if (currentUser) {
            try {
                // User is logged in, try Firestore first
                const cartRef = doc(db, "users", currentUser.uid, "cart", "current");
                const cartDoc = await getDoc(cartRef);

                if (cartDoc.exists()) {
                    const cartData = cartDoc.data();
                    const cartItems = cartData.items || [];

                    if (enrichData) {
                        // Enrich cart items with fresh product data (only when requested)
                        const enrichedItems = await enrichCartWithFreshData(cartItems);

                        // Calculate fresh subtotal based on current prices
                        const subtotal = enrichedItems.reduce((total, item) => {
                            return total + (item.price * item.quantity);
                        }, 0);

                        return {
                            items: enrichedItems,
                            subtotal: subtotal,
                            storeId: cartData.storeId || DEFAULT_STORE_ID
                        };
                    } else {
                        // Fast path - use stored data without enrichment
                        const subtotal = cartItems.reduce((total, item) => {
                            // Use stored price or fallback to 0 if not available
                            const price = item.price || 0;
                            return total + (price * item.quantity);
                        }, 0);

                        return {
                            items: cartItems,
                            subtotal: subtotal,
                            storeId: cartData.storeId || DEFAULT_STORE_ID
                        };
                    }
                }
            } catch (firestoreError) {
                // console.error("Firestore permission error when getting cart:", firestoreError);
                // Fall back to localStorage if Firestore fails
            }
        }

        // If we reach here, check localStorage
        let cart;
        try {
            cart = JSON.parse(localStorage.getItem('guestCart'));

            // ADDED: Ensure correct storeId in localStorage cart
            if (cart && cart.storeId !== DEFAULT_STORE_ID) {
                cart.storeId = DEFAULT_STORE_ID;
                localStorage.setItem('guestCart', JSON.stringify(cart));
                // console.log(`Updated cart storeId to ${DEFAULT_STORE_ID}`);
            }
        } catch (error) {
            console.error("Error parsing guest cart:", error);
            cart = null;
        }

        // Ensure cart has proper structure
        if (!cart || !Array.isArray(cart.items)) {
            cart = {
                items: [],
                subtotal: 0,
                storeId: DEFAULT_STORE_ID
            };
        }

        // Always ensure there's a storeId
        if (!cart.storeId) {
            cart.storeId = DEFAULT_STORE_ID;
        }

        if (enrichData) {
            // Enrich localStorage cart items with fresh product data (only when requested)
            const enrichedItems = await enrichCartWithFreshData(cart.items);

            // Calculate fresh subtotal based on current prices
            const subtotal = enrichedItems.reduce((total, item) => {
                return total + (item.price * item.quantity);
            }, 0);

            return {
                items: enrichedItems,
                subtotal: subtotal,
                storeId: cart.storeId
            };
        } else {
            // Fast path - use stored data without enrichment
            const subtotal = cart.items.reduce((total, item) => {
                // Use stored price or fallback to 0 if not available
                const price = item.price || 0;
                return total + (price * item.quantity);
            }, 0);

            return {
                items: cart.items,
                subtotal: subtotal,
                storeId: cart.storeId
            };
        }
    } catch (error) {
        console.error("Error getting cart:", error);
        // Return empty cart as fallback in case of other errors
        return { items: [], subtotal: 0, storeId: DEFAULT_STORE_ID };
    }
};

/**
 * Update item quantity in cart
 * @param {string} productId - Product ID
 * @param {number} quantity - New quantity
 * @param {string} variantId - Variant ID (if applicable)
 * @param {object} currentUser - Current user (if logged in)
 * @returns {Promise<object>} Updated cart
 */
export const updateCartItemQuantity = async (productId, quantity, variantId = null, currentUser = null) => {
    try {
        if (currentUser) {
            // User is logged in, update in Firestore
            const cartRef = doc(db, "users", currentUser.uid, "cart", "current");
            const cartDoc = await getDoc(cartRef);

            if (!cartDoc.exists()) {
                throw new Error("Cart not found");
            }

            const cartData = cartDoc.data();

            if (quantity <= 0) {
                // Remove item
                cartData.items = cartData.items.filter(item =>
                    !(item.productId === productId && item.variantId === variantId)
                );
            } else {
                // Update quantity
                const itemIndex = cartData.items.findIndex(item =>
                    item.productId === productId && item.variantId === variantId
                );

                if (itemIndex >= 0) {
                    cartData.items[itemIndex].quantity = quantity;
                }
            }

            // Don't calculate subtotal here - it will be calculated fresh in getCart()
            cartData.updatedAt = serverTimestamp();

            // Save to Firestore
            await setDoc(cartRef, cartData);

            return {
                items: cartData.items,
                subtotal: cartData.subtotal,
                storeId: cartData.storeId || DEFAULT_STORE_ID
            };
        } else {
            // Guest user, update in localStorage
            let cart = JSON.parse(localStorage.getItem('guestCart')) || {
                items: [],
                subtotal: 0,
                storeId: DEFAULT_STORE_ID
            };

            if (quantity <= 0) {
                // Remove item
                cart.items = cart.items.filter(item =>
                    !(item.productId === productId && item.variantId === variantId)
                );
            } else {
                // Update quantity
                const itemIndex = cart.items.findIndex(item =>
                    item.productId === productId && item.variantId === variantId
                );

                if (itemIndex >= 0) {
                    cart.items[itemIndex].quantity = quantity;
                }
            }

            // Don't calculate subtotal here - it will be calculated fresh in getCart()
            cart.subtotal = 0; // Placeholder - will be calculated fresh
            cart.updatedAt = new Date().toISOString();

            // Save to localStorage
            localStorage.setItem('guestCart', JSON.stringify(cart));

            return cart;
        }
    } catch (error) {
        console.error("Error updating cart item:", error);
        throw error;
    }
};

/**
 * Remove item from cart
 * @param {string} productId - Product ID
 * @param {string} variantId - Variant ID (if applicable)
 * @param {object} currentUser - Current user (if logged in)
 * @returns {Promise<object>} Updated cart
 */
export const removeCartItem = async (productId, variantId = null, currentUser = null) => {
    return updateCartItemQuantity(productId, 0, variantId, currentUser);
};

/**
 * Clear the entire cart
 * @param {object} currentUser - Current user (if logged in)
 * @returns {Promise<object>} Empty cart
 */
export const clearCart = async (currentUser = null) => {
    try {
        const emptyCart = {
            items: [],
            subtotal: 0,
            storeId: DEFAULT_STORE_ID
        };

        if (currentUser) {
            // User is logged in, clear in Firestore
            const cartRef = doc(db, "users", currentUser.uid, "cart", "current");
            await setDoc(cartRef, {
                ...emptyCart,
                updatedAt: serverTimestamp()
            });
        } else {
            // Guest user, clear in localStorage
            localStorage.setItem('guestCart', JSON.stringify({
                ...emptyCart,
                updatedAt: new Date().toISOString()
            }));
        }

        return emptyCart;
    } catch (error) {
        console.error("Error clearing cart:", error);
        throw error;
    }
};

/**
 * Migrate guest cart to user cart after login with improved error handling
 * @param {string} userId - User ID
 * @returns {Promise<object>} Updated cart
 */
export const migrateGuestCart = async (userId) => {
    try {
        if (!userId) {
            throw new Error("User ID is required to migrate cart");
        }

        // Get guest cart from localStorage with better error handling
        let guestCart;
        try {
            guestCart = JSON.parse(localStorage.getItem('guestCart')) || {
                items: [],
                subtotal: 0,
                storeId: DEFAULT_STORE_ID  // CHANGED: Always use DEFAULT_STORE_ID
            };

            // Force correct storeId on all items in guest cart
            if (guestCart.items && Array.isArray(guestCart.items)) {
                for (let item of guestCart.items) {
                    item.storeId = DEFAULT_STORE_ID;
                }
            }

            // console.log("Found guest cart to migrate:", guestCart);
        } catch (parseError) {
            // console.error("Error parsing guest cart:", parseError);
            localStorage.removeItem('guestCart');
            guestCart = { items: [], subtotal: 0, storeId: DEFAULT_STORE_ID };
        }

        if (guestCart.items.length === 0) {
            // console.log("Guest cart is empty, nothing to migrate");
            return { items: [], subtotal: 0, storeId: DEFAULT_STORE_ID };
        }

        // Get user cart from Firestore
        try {
            const cartRef = doc(db, "users", userId, "cart", "current");
            const cartDoc = await getDoc(cartRef);

            let userCart = {
                items: [],
                subtotal: 0,
                storeId: guestCart.storeId || DEFAULT_STORE_ID
            };

            if (cartDoc.exists()) {
                const cartData = cartDoc.data();
                if (cartData && Array.isArray(cartData.items)) {
                    userCart = cartData;
                } else {
                    // console.log("Invalid user cart structure in Firestore:", cartData);
                }
            }

            // Ensure items is an array
            if (!Array.isArray(userCart.items)) {
                userCart.items = [];
            }

            // Merge carts
            for (const guestItem of guestCart.items) {
                // Validate guest item
                if (!guestItem.productId) continue;

                const existingItemIndex = userCart.items.findIndex(item =>
                    item.productId === guestItem.productId &&
                    item.variantId === guestItem.variantId
                );

                if (existingItemIndex >= 0) {
                    // Update quantity of existing item
                    userCart.items[existingItemIndex].quantity += (parseInt(guestItem.quantity) || 0);
                    // Preserve price info from guest item (guest cart should have current prices)
                    if (guestItem.price !== undefined) {
                        userCart.items[existingItemIndex].price = guestItem.price;
                        userCart.items[existingItemIndex].originalPrice = guestItem.originalPrice || guestItem.price;
                    }
                } else {
                    // Add new item with stored display data for fast rendering
                    userCart.items.push({
                        productId: guestItem.productId,
                        variantId: guestItem.variantId,
                        quantity: parseInt(guestItem.quantity) || 1,
                        storeId: guestItem.storeId || DEFAULT_STORE_ID,
                        dateAdded: guestItem.dateAdded || new Date().toISOString(),
                        displayVariant: guestItem.displayVariant,
                        // Keep stored display data for fast cart rendering
                        name: guestItem.name,
                        image: guestItem.image,
                        // PRESERVE price information from guest cart for immediate display
                        price: guestItem.price || 0,
                        originalPrice: guestItem.originalPrice || guestItem.price || 0,
                        inStock: guestItem.inStock !== undefined ? guestItem.inStock : true
                    });
                }
            }

            // Calculate initial subtotal from migrated items (with stored prices)
            const initialSubtotal = userCart.items.reduce((total, item) => {
                const price = item.price || 0;
                return total + (price * item.quantity);
            }, 0);

            userCart.subtotal = initialSubtotal; // Use calculated subtotal instead of 0
            userCart.updatedAt = serverTimestamp();

            // Save to Firestore
            await setDoc(cartRef, userCart);
            // console.log("Guest cart successfully migrated to user account");

            // Clear guest cart
            localStorage.removeItem('guestCart');

            return {
                items: userCart.items,
                subtotal: userCart.subtotal, // Use the calculated subtotal
                storeId: userCart.storeId
            };
        } catch (firestoreError) {
            console.error("Error accessing Firestore for cart migration:", firestoreError);
            // Don't clear the guest cart in case of error so we can try again later
            throw firestoreError;
        }
    } catch (error) {
        console.error("Error migrating guest cart:", error);
        return { items: [], subtotal: 0, storeId: DEFAULT_STORE_ID };
    }
};


